import React, { useState } from "react"
import styled, { css } from "styled-components"
import { ModalHeader } from "../Modal"
import { bp } from "../../layouts/Styles"
import SendArrowSVG from "../../assets/icons/line-arrow.svg"
import Arrow from "../../assets/icons/arrow.svg"
import Checkbox from "../Checkbox"
import { Collapse } from "react-collapse"
import { useTranslation } from "gatsby-plugin-react-i18next"

export enum AnalyticsOption {
  GoogleAnalytics = `ganalytics`,
  GoogleAdWords = `gadwords`,
}

export type CookieOptions = { [key: string]: boolean }

interface SettingsRow {
  title: string
  description: string
  options: { [key: string]: string }
}

export const defaultCookieOptions = {
  [AnalyticsOption.GoogleAnalytics]: true,
  [AnalyticsOption.GoogleAdWords]: false,
}

interface CookieSettingsProps {
  onClose?: () => void
  onSave?: (options: CookieOptions) => void
  savedOptions?: CookieOptions
}

const CookieSettings: React.FC<CookieSettingsProps> = ({
  onClose,
  onSave,
  savedOptions,
}) => {
  const [expandedIdx, setExpandedIdx] = useState(0)
  const [enabledOptions, setEnabledOptions] = useState<CookieOptions>({})
  const { t } = useTranslation()

  const rows: SettingsRow[] = [
    {
      title: t("Marketing Cookies"),
      description: t(
        "Diese Cookies setzen wir ein, um zielgruppenorientierte Werbung zu erstellen und das Nutzungsverhalten auf dieser Website zu analysieren."
      ),
      options: {
        [AnalyticsOption.GoogleAnalytics]: t("Google Analytics"),
      },
    },
  ]
  return (
    <CookieSettingsContainer>
      <ModalHeader
        title={t("Cookie Einstellungen") ?? ""}
        onClose={() => onClose?.()}
      />

      <div className="settings">
        {rows.map((row, idx) => (
          <CookieSettingsRow
            key={idx}
            rowData={row}
            savedOptions={savedOptions}
            expanded={expandedIdx === idx}
            onClick={() => setExpandedIdx(idx)}
            onChangeOption={(option, value) =>
              setEnabledOptions(options => ({ ...options, [option]: value }))
            }
          />
        ))}
      </div>

      <button className="save" onClick={() => onSave?.(enabledOptions)}>
        <span>{t("Auswahl speichern und zustimmen")}</span>
        <SendArrowSVG />
      </button>
    </CookieSettingsContainer>
  )
}

const CookieSettingsContainer = styled.div`
  background: ${({ theme }) => theme.background.default};
  font-size: 1.2rem;

  width: 44rem;
  max-width: 100%;
  padding: 2rem;

  h3 {
    font-size: 1.8rem;
  }

  .settings {
    margin-top: -1.5rem;
  }

  .save {
    font-size: 0.9em;
    padding-top: 2em;
    font-weight: 500;
    display: flex;
    justify-content: space-between;
    width: 100%;
    padding-right: 2.4em;
    cursor: pointer;

    span {
      color: ${({ theme }) => theme.lightText};
    }

    svg {
      width: 1.4em;
    }
  }
`

interface CookieSettingsRowProps {
  rowData: SettingsRow
  expanded?: boolean
  onClick?: () => void
  onChangeOption?: (key: string, checked: boolean) => void
  savedOptions?: CookieOptions
}

const CookieSettingsRow: React.FC<CookieSettingsRowProps> = ({
  rowData,
  expanded = false,
  onClick,
  onChangeOption,
  savedOptions = {},
}) => (
  <CookieSettingsRowContainer expanded={expanded}>
    <button className="title" onClick={() => onClick?.()}>
      <h5>{rowData.title}</h5>

      <div className="expand">
        <ArrowIcon />
      </div>
    </button>

    <Collapse isOpened={expanded}>
      <div className="description">
        <p>{rowData.description}</p>

        {Object.keys(rowData.options).map((opt, idx) => (
          <Checkbox
            key={idx}
            defaultChecked={
              savedOptions[opt] ?? defaultCookieOptions[opt as AnalyticsOption]
            }
            onChange={checked => onChangeOption?.(opt, checked)}
          >
            <span>{rowData.options[opt]}</span>
          </Checkbox>
        ))}
      </div>
    </Collapse>
  </CookieSettingsRowContainer>
)

const CookieSettingsRowContainer = styled.div<{ expanded?: boolean }>`
  border-bottom: 1px solid ${({ theme }) => theme.lightGrey};

  .title {
    cursor: pointer;
    font-size: 0.95rem;
    padding: 1.65rem 0;
    display: flex;
    width: 100%;
    justify-content: space-between;
    align-items: center;

    .expand {
      margin-right: 0.8rem;
    }

    svg {
      transition: transform 0.15s linear;

      ${props =>
        props.expanded &&
        css`
          transform: rotateZ(90deg);
        `}
    }
  }

  .ReactCollapse--collapse {
    transition: height 200ms;
  }

  .description {
    display: flex;
    flex-direction: column;
    overflow: hidden;

    p {
      font-size: 0.75rem;
      color: ${({ theme }) => theme.lightText};
      line-height: 1.5;
      margin-bottom: 2.4rem;
    }

    label {
      margin-bottom: 1.4rem;
      font-size: 0.8rem;
      padding-left: 3.5rem;
    }
  }

  ${bp("mobile")`
    font-size: 1.4rem;
  `}
`

const ArrowIcon = styled(Arrow)`
  width: 0.7rem;
  transform: rotate(90deg);
`
export default CookieSettings
